<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { SidebarMenuItem, MenuPermission, MenuClickEvent } from '@/types/sidebar-menu.types'
import JDynamicMenuItem from './JSidebarSimple/JDynamicMenuItem.vue'
import { cn } from '@/lib/utils'
import { filterMenuItems, getExpandedKeysForSearch } from '@/lib/menu-utils'

/**
 * JSidebarSimple - 간단한 사이드바 컴포넌트
 * Simple Sidebar Component
 * 
 * @description
 * 다단계 메뉴 구조를 지원하는 기본 사이드바 컴포넌트입니다.
 * 권한 체크, 메뉴 검색 등의 기능을 제공합니다.
 * 
 * @example
 * ```vue
 * <JSidebarSimple
 *   :menu-items="menuItems"
 *   :permissions="userPermissions"
 *   @menu-click="handleMenuClick"
 * />
 * ```
 * 
 * @example JSON 메뉴 데이터 예시
 * ```json
 * [
 *   {
 *     "label": "대시보드",
 *     "icon": "house",
 *     "menuType": "L",
 *     "menuKey": 1,
 *     "path": "/dashboard"
 *   },
 *   {
 *     "label": "재고 관리",
 *     "icon": "package",
 *     "menuType": "F",
 *     "menuKey": 2,
 *     "children": [
 *       {
 *         "label": "재고 현황",
 *         "menuType": "L",
 *         "menuKey": 21,
 *         "path": "/inventory/status"
 *       },
 *       {
 *         "label": "입고 관리",
 *         "menuType": "L",
 *         "menuKey": 22,
 *         "path": "/inventory/receiving"
 *       }
 *     ]
 *   }
 * ]
 * ```
 */

type StyleType = 'default' | 'minimal'

const props = withDefaults(
  defineProps<{
    /** 메뉴 아이템 목록 */
    menuItems: SidebarMenuItem[]
    /** 권한 목록 */
    permissions?: MenuPermission[]
    /** 검색어 */
    searchQuery?: string
    /** 스타일 타입 */
    styletype?: StyleType
    /** 추가 CSS 클래스 */
    class?: string
    /** 너비 */
    width?: string
    /** 표시 여부 */
    isVisible?: boolean
  }>(),
  {
    permissions: () => [],
    searchQuery: '',
    styletype: 'default',
    width: '250px',
    isVisible: true,
  },
)

const emit = defineEmits<{
  /** 메뉴 클릭 이벤트 */
  menuClick: [event: MenuClickEvent]
}>()

// vue-router가 설정되지 않은 경우를 대비 (Storybook에서 router가 제공됨)
const route = useRoute()

/**
 * 현재 활성화된 경로
 */
const activePath = computed(() => route.path)

/**
 * 확장된 메뉴 키 목록 (Set)
 */
const expandedKeys = ref<Set<number | string>>(new Set())

/**
 * 검색어로 필터링된 메뉴 아이템
 * 재귀적으로 children까지 검색
 */
const filteredMenuItems = computed(() => {
  return filterMenuItems(props.menuItems, props.searchQuery || '')
})

/**
 * 검색 결과에 따라 부모 메뉴 자동 확장
 * computed 외부에서 watch를 통해 처리
 */
watch(
  () => filteredMenuItems.value,
  (filtered) => {
    if (!props.searchQuery || props.searchQuery.trim() === '') {
      return
    }

    // 검색 결과에서 매칭된 하위 메뉴가 있는 부모를 찾아 확장
    const keysToExpand = getExpandedKeysForSearch(filtered)
    keysToExpand.forEach(key => {
      expandedKeys.value.add(key)
    })
  },
  { immediate: false }
)

/**
 * 확장 상태 변경 핸들러
 */
const handleExpandChange = (menuKey: number | string | undefined, expanded: boolean) => {
  if (!menuKey) return

  if (expanded) {
    expandedKeys.value.add(menuKey)
  } else {
    expandedKeys.value.delete(menuKey)
  }
}

/**
 * 메뉴 클릭 핸들러
 */
const handleMenuClick = (event: MenuClickEvent) => {
  emit('menuClick', event)
}

/**
 * 스타일 프리셋
 */
const STYLE_PRESETS: Record<StyleType, {
  containerClass: string
  menuPaddingClass: string
}> = {
  default: {
    containerClass: 'h-full bg-background border-r border-border overflow-y-auto',
    menuPaddingClass: 'p-1.5',
  },
  minimal: {
    containerClass: 'h-full bg-background border-r border-border overflow-y-auto',
    menuPaddingClass: 'p-1',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

/**
 * 루트 클래스
 */
const rootClasses = computed(() => {
  return cn(
    preset.value.containerClass,
    props.class
  )
})
</script>

<template>
  <Transition name="slide">
    <aside v-show="props.isVisible" :class="rootClasses" :style="{ width }">
      <div :class="cn(preset.menuPaddingClass, 'space-y-0.5')">
        <JDynamicMenuItem
          v-for="(item, index) in filteredMenuItems"
          :key="item.menuKey || item.label || index"
          :item="item"
          :level="0"
          :permissions="permissions"
          :active-path="activePath"
          :expanded-keys="expandedKeys"
          :styletype="styletype"
          @menu-click="handleMenuClick"
          @expand-change="handleExpandChange"
        />
      </div>
    </aside>
  </Transition>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
</style>
